import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Mail, MessageCircle, HelpCircle, FileText, Shield, ArrowLeft } from 'lucide-react';

const Support = () => {
  const supportEmail = 'kidquotesapp@gmail.com';
  const appVersion = '1.0.0';

  const handleContactSupport = () => {
    const subject = encodeURIComponent('KidQuotes Support Request');
    const body = encodeURIComponent(`Hi KidQuotes Support,\n\nI need help with:\n\n\n---\nApp Version: ${appVersion}\n`);
    window.location.href = `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 overflow-y-auto">
      <div className="container max-w-4xl mx-auto px-4 py-8 pb-16">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Support & Help</h1>
          <p className="text-lg text-gray-600">
            Get assistance with KidQuotes or learn more about our app
          </p>
        </div>

        {/* Contact Support Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Contact Support
            </CardTitle>
            <CardDescription>
              We're here to help! Reach out with any questions or issues.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="mb-4">
                For support inquiries, please email us at:
              </p>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <a
                  href={`mailto:${supportEmail}`}
                  className="text-primary hover:underline font-medium"
                >
                  {supportEmail}
                </a>
                <Button onClick={handleContactSupport} size="sm">
                  Send Email
                </Button>
              </div>
            </div>
            <Separator />
            <p className="text-sm text-muted-foreground">
              We typically respond within 24-48 hours. Please include as much detail as possible about your issue to help us assist you better.
            </p>
          </CardContent>
        </Card>

        {/* FAQs Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5" />
              Frequently Asked Questions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">What is KidQuotes?</h3>
              <p className="text-sm text-muted-foreground">
                KidQuotes is an app designed to help parents capture and preserve memorable quotes, milestones, and moments from their children's lives. Share these precious memories with family members and create a lasting record of your child's growth.
              </p>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-2">How do I create an account?</h3>
              <p className="text-sm text-muted-foreground">
                You can create an account by downloading the app and tapping "Sign Up" on the login screen. You'll need to provide an email address and create a password.
              </p>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-2">How do I add a child?</h3>
              <p className="text-sm text-muted-foreground">
                After logging in, navigate to the "Children" section and tap the "+" button to add a new child. You'll be able to add their name, birthdate, and a photo.
              </p>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-2">Can I share access with family members?</h3>
              <p className="text-sm text-muted-foreground">
                Yes! KidQuotes supports family sharing. You can invite other family members to join your family group and they'll be able to view and add quotes and milestones for your children.
              </p>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-2">Is my data secure?</h3>
              <p className="text-sm text-muted-foreground">
                Absolutely. We take your privacy and security seriously. All data is encrypted and stored securely. We never share your personal information with third parties. Read our{' '}
                <Link to="/privacy" className="text-primary hover:underline">
                  Privacy Policy
                </Link>
                {' '}for more details.
              </p>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-2">How do I delete my account?</h3>
              <p className="text-sm text-muted-foreground">
                You can delete your account at any time by going to Settings → Account Settings → Delete tab. This will permanently remove all your data including quotes, milestones, children, and family groups. This action cannot be undone.
              </p>
            </div>

            <Separator />

            <div>
              <h3 className="font-semibold mb-2">I'm having technical issues. What should I do?</h3>
              <p className="text-sm text-muted-foreground">
                Please email us at {supportEmail} with details about the issue you're experiencing. Include information about your device, app version, and steps to reproduce the problem if possible.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Additional Resources Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Additional Resources
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Link to="/privacy">
                <Button variant="outline" className="w-full justify-start gap-2">
                  <Shield className="h-4 w-4" />
                  Privacy Policy
                </Button>
              </Link>
              <Link to="/terms">
                <Button variant="outline" className="w-full justify-start gap-2">
                  <FileText className="h-4 w-4" />
                  Terms of Service
                </Button>
              </Link>
            </div>

            <Separator />

            <div className="text-sm text-muted-foreground">
              <p className="font-medium mb-1">App Version</p>
              <p>{appVersion}</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p>© 2025 KidQuotes. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Support;
